import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Copy } from "lucide-react";
import { DAYS, dayError, copyDay } from "@/lib/businessHours";

function CopyMenu({ from, onCopy }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid={`hours-copy-${from}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><Copy className="h-3 w-3" /> Copy to…</button>;
  return (
    <div className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid={`hours-copy-panel-${from}`}>
      <div className="flex flex-wrap gap-3">
        {DAYS.filter(([k]) => k !== from).map(([k, l]) => (
          <label key={k} className="flex items-center gap-1 text-xs"><Checkbox checked={sel.includes(k)} onCheckedChange={(v) => setSel(v ? [...sel, k] : sel.filter((x) => x !== k))} data-testid={`hours-copy-${from}-to-${k}`} />{l.slice(0, 3)}</label>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" className="h-7 bg-slate-900 text-white" disabled={!sel.length} data-testid={`hours-copy-apply-${from}`} onClick={() => { onCopy(sel); setOpen(false); setSel([]); }}>Apply</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

export function WeeklyHoursEditor({ week, onChange, disabled, prefix = "hours" }) {
  const setDay = (k, d) => onChange({ ...week, [k]: d });
  return (
    <div className="divide-y divide-slate-100" data-testid={`${prefix}-editor`}>
      {DAYS.map(([k, label]) => {
        const d = week[k];
        const err = dayError(d);
        return (
          <div key={k} className="flex flex-wrap items-start gap-3 py-3" data-testid={`${prefix}-day-${k}`}>
            <div className="flex w-36 shrink-0 items-center gap-2">
              <Switch checked={!d.closed} disabled={disabled} aria-label={`${label} open`} data-testid={`${prefix}-open-toggle-${k}`}
                onCheckedChange={(v) => setDay(k, v ? { closed: false, periods: d.periods.length ? d.periods : [{ open: "09:00", close: "18:00", overnight: false }] } : { closed: true, periods: [] })} />
              <span className="text-sm font-medium text-slate-700">{label}</span>
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              {d.closed ? <span className="text-sm text-slate-400">Closed</span> : d.periods.map((p, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Input type="time" value={p.open} disabled={disabled} className="h-8 w-28" data-testid={`${prefix}-${k}-${i}-open`} onChange={(e) => setDay(k, { ...d, periods: d.periods.map((x, j) => (j === i ? { ...x, open: e.target.value } : x)) })} />
                  <span className="text-slate-400">–</span>
                  <Input type="time" value={p.close} disabled={disabled} className="h-8 w-28" data-testid={`${prefix}-${k}-${i}-close`} onChange={(e) => setDay(k, { ...d, periods: d.periods.map((x, j) => (j === i ? { ...x, close: e.target.value } : x)) })} />
                  <label className="flex items-center gap-1 text-xs text-slate-500"><Checkbox checked={!!p.overnight} disabled={disabled} data-testid={`${prefix}-${k}-${i}-overnight`} onCheckedChange={(v) => setDay(k, { ...d, periods: d.periods.map((x, j) => (j === i ? { ...x, overnight: !!v } : x)) })} />Overnight</label>
                  {!disabled && d.periods.length > 1 && <button type="button" aria-label="Remove period" data-testid={`${prefix}-${k}-${i}-remove`} onClick={() => setDay(k, { ...d, periods: d.periods.filter((_, j) => j !== i) })} className="rounded p-1 text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {err && <p className="text-xs text-rose-600" role="alert" data-testid={`${prefix}-error-${k}`}>{err}</p>}
              {!disabled && (
                <div className="flex flex-wrap items-center gap-4">
                  {!d.closed && d.periods.length < 6 && <button type="button" data-testid={`${prefix}-add-period-${k}`} onClick={() => setDay(k, { ...d, periods: [...d.periods, { open: "", close: "", overnight: false }] })} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"><Plus className="h-3 w-3" /> Add period</button>}
                  <CopyMenu from={k} onCopy={(targets) => onChange(copyDay(week, k, targets))} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
